import React from "react"
import {
  PenLine,
  AlignLeft,
  Hash,
  List,
  CircleDot,
  CheckSquare,
  Calendar,
  Upload,
} from "lucide-react"
import type { FormField } from "@/types/requestForm"
import type { TFunction } from "i18next"

import {
  ShortTextFieldRenderer,
  validateShortTextField,
} from "./ShortTextField"
import { LongTextFieldRenderer, validateLongTextField } from "./LongTextField"
import { NumberFieldRenderer, validateNumberField } from "./NumberField"
import {
  DropdownFieldRenderer,
  validateDropdownField,
  serializeDropdownField,
} from "./DropdownField"
import {
  RadioFieldRenderer,
  validateRadioField,
  serializeRadioField,
} from "./RadioField"
import {
  CheckboxFieldRenderer,
  validateCheckboxField,
  serializeCheckboxField,
} from "./CheckboxField"
import { DateFieldRenderer, validateDateField } from "./DateField"

import { ShortTextFieldEditor } from "./ShortTextField"
import { LongTextFieldEditor } from "./LongTextField"
import { NumberFieldEditor } from "./NumberField"
import { DropdownFieldEditor } from "./DropdownField"
import { RadioFieldEditor } from "./RadioField"
import { CheckboxFieldEditor } from "./CheckboxField"
import { DateFieldEditor } from "./DateField"
import { FileFieldEditor, FileFieldRenderer } from "./FileField"

export type FieldType =
  | "shortText"
  | "longText"
  | "number"
  | "dropdown"
  | "radio"
  | "checkbox"
  | "date"
  | "file"

// Renderer props - common structure for all field renderers
// Made flexible to accept union types for value/onChange and any FormField subset
export interface FieldRendererProps {
  field: {
    question: string
    placeholder: string
    required: boolean
    [key: string]: unknown
  }
  mode: "preview" | "runtime"
  value?: string | number | boolean | string[]
  onChange?: (v: string | number | boolean | string[]) => void
  disabled?: boolean
  error?: string
  contextId?: string // Unique context identifier to prevent React from reusing component instances
}

// Editor props - matches BaseFieldProps structure used by all field editors
// Note: Components accept specific field types, but we use FormField here for registry compatibility
// FormField already contains all required properties (name, question, label, required, placeholder, type)
// and optional properties (min, max, step, options, etc.)
export interface FieldEditorProps {
  field: FormField
  onChange: (next: FormField) => void
  disabled?: boolean
  showPlaceholder?: boolean
}

// Type-safe helper functions to register components
// These provide better type safety than 'as unknown as' by:
// 1. Ensuring components are React components (type-checked at call site)
// 2. Centralizing type assertions in one place with clear documentation
// 3. Making it easier to refactor if types change
//
// Note: Type assertions are necessary here because:
// - Renderers have specific onChange signatures (e.g., (v: string) => void) but
//   FieldRendererProps uses a union type for flexibility
// - Editors accept specific field types (e.g., ShortTextField) but FieldEditorProps
//   uses FormField for registry compatibility
// Runtime safety is ensured by callers passing correctly-typed props
function registerRenderer(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Intentional: Accept any React component and assert to FieldRendererProps
  Component: React.ComponentType<any>
): React.ComponentType<FieldRendererProps> {
  return Component as React.ComponentType<FieldRendererProps>
}

function registerEditor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Intentional: Accept any React component and assert to FieldEditorProps
  Component: React.ComponentType<any>
): React.ComponentType<FieldEditorProps> {
  return Component as React.ComponentType<FieldEditorProps>
}

export type FieldValidator = (
  field: FormField,
  value: unknown,
  t: TFunction
) => string | undefined

export interface FieldSpec {
  type: FieldType
  icon: React.FC<{ className?: string }>
  displayNameKey: string
  renderer: React.ComponentType<FieldRendererProps>
  editor: React.ComponentType<FieldEditorProps>
  validator: FieldValidator
  /**
   * Serialize a raw runtime value into the shape that should be stored
   * in the submission payload. If it returns undefined, the field will
   * be omitted from the submission.
   *
   * When omitted, callers should use a default serializer that returns
   * the raw value unchanged.
   */
  serialize?: (field: FormField, raw: unknown) => unknown
}

export const fieldRegistry: Record<FieldType, FieldSpec> = {
  file: {
    type: "file",
    icon: (p) => <Upload className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.file",
    renderer: registerRenderer(FileFieldRenderer),
    editor: registerEditor(FileFieldEditor),
    validator: () => undefined,
  },
  shortText: {
    type: "shortText",
    icon: (p) => <PenLine className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.shortText",
    renderer: registerRenderer(ShortTextFieldRenderer),
    editor: registerEditor(ShortTextFieldEditor),
    validator: validateShortTextField,
  },
  longText: {
    type: "longText",
    icon: (p) => <AlignLeft className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.longText",
    renderer: registerRenderer(LongTextFieldRenderer),
    editor: registerEditor(LongTextFieldEditor),
    validator: validateLongTextField,
  },
  number: {
    type: "number",
    icon: (p) => <Hash className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.number",
    renderer: registerRenderer(NumberFieldRenderer),
    editor: registerEditor(NumberFieldEditor),
    validator: validateNumberField,
  },
  dropdown: {
    type: "dropdown",
    icon: (p) => <List className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.dropdown",
    renderer: registerRenderer(DropdownFieldRenderer),
    editor: registerEditor(DropdownFieldEditor),
    validator: validateDropdownField,
    serialize: serializeDropdownField,
  },
  radio: {
    type: "radio",
    icon: (p) => <CircleDot className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.radio",
    renderer: registerRenderer(RadioFieldRenderer),
    editor: registerEditor(RadioFieldEditor),
    validator: validateRadioField,
    serialize: serializeRadioField,
  },
  checkbox: {
    type: "checkbox",
    icon: (p) => <CheckSquare className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.checkbox",
    renderer: registerRenderer(CheckboxFieldRenderer),
    editor: registerEditor(CheckboxFieldEditor),
    validator: validateCheckboxField,
    serialize: serializeCheckboxField,
  },
  date: {
    type: "date",
    icon: (p) => <Calendar className={p.className} />,
    displayNameKey: "workspace.agent.basicForm.formBuilder.types.date",
    renderer: registerRenderer(DateFieldRenderer),
    editor: registerEditor(DateFieldEditor),
    validator: validateDateField,
  },
}

export const allFieldTypes: FieldType[] = Object.keys(
  fieldRegistry
) as FieldType[]
